import React, { useMemo, useState } from "react";
import Text from "../components/atoms/Text";
import ProductCard from "../components/molecules/ProductCard";
import products from "../data/products.json";

const priceOptions = [
  { value: "all", label: "Select Price" },
  { value: "0-10", label: "$0 - $10" },
  { value: "10-15", label: "$10 - $15" },
  { value: "15-20", label: "$15 - $20" },
];

const ratingOptions = [
  { value: "all", label: "All Rating" },
  { value: "1-1.9", label: "1 - 1.9" },
  { value: "2-2.9", label: "2 - 2.9" },
  { value: "3-3.9", label: "3 - 3.9" },
  { value: "4-4.4", label: "4 - 4.4" },
  { value: "4.5-5", label: "4.5 - 5" },
];

const sortOptions = [
  { value: "latest", label: "Sort by: Latest" },
  { value: "priceLowHigh", label: "Price: Low to High" },
  { value: "priceHighLow", label: "Price: High to Low" },
  { value: "ratingHighLow", label: "Rating: High to Low" },
];

const showOptions = [
  { value: 16, label: "Show: 16" },
  { value: 12, label: "Show: 12" },
  { value: 8, label: "Show: 8" },
];

export default function Shop() {
  const [priceFilter, setPriceFilter] = useState("all");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [sortBy, setSortBy] = useState("latest");
  const [showCount, setShowCount] = useState(16);
  const [currentPage, setCurrentPage] = useState(1);

  const filteredProducts = useMemo(() => {
    let nextProducts = [...products];

    if (priceFilter !== "all") {
      const [minPrice, maxPrice] = priceFilter.split("-").map(Number);
      nextProducts = nextProducts.filter(
        (product) => product.price >= minPrice && product.price <= maxPrice,
      );
    }

    if (ratingFilter !== "all") {
      const [minRating, maxRating] = ratingFilter.split("-").map(Number);
      nextProducts = nextProducts.filter(
        (product) => product.rating >= minRating && product.rating <= maxRating,
      );
    }

    switch (sortBy) {
      case "priceLowHigh":
        nextProducts.sort((a, b) => a.price - b.price);
        break;
      case "priceHighLow":
        nextProducts.sort((a, b) => b.price - a.price);
        break;
      case "ratingHighLow":
        nextProducts.sort((a, b) => b.rating - a.rating);
        break;
      case "latest":
      default:
        nextProducts.sort((a, b) => b.id - a.id);
        break;
    }

    return nextProducts;
  }, [priceFilter, ratingFilter, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / showCount));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * showCount;
  const visibleProducts = filteredProducts.slice(
    startIndex,
    startIndex + showCount,
  );

  const activeFilters = [
    priceFilter !== "all" &&
      priceOptions.find((option) => option.value === priceFilter)?.label,
    ratingFilter !== "all" &&
      ratingOptions.find((option) => option.value === ratingFilter)?.label,
  ].filter(Boolean);

  const paginationItems = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    if (safeCurrentPage <= 4) {
      return [1, 2, 3, 4, 5, "...", totalPages];
    }

    if (safeCurrentPage >= totalPages - 3) {
      return [
        1,
        "...",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    return [
      1,
      "...",
      safeCurrentPage - 1,
      safeCurrentPage,
      safeCurrentPage + 1,
      "...",
      totalPages,
    ];
  }, [safeCurrentPage, totalPages]);

  const selectStyles = {
    padding: "12px 52px 12px 14px",
    borderRadius: "6px",
    border: "1px solid var(--gray1)",
    backgroundColor: "var(--white)",
    backgroundImage:
      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23666666' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 16px center",
    backgroundSize: "12px 8px",
    color: "var(--gray6)",
    fontFamily: "inherit",
    fontSize: "14px",
    lineHeight: "150%",
    minWidth: "165px",
    cursor: "pointer",
    outline: "none",
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
  };

  const resetToFirstPage = () => setCurrentPage(1);

  return (
    <div
      className="shop-page"
      style={{
        padding: "40px 300px 100px",
        backgroundColor: "var(--white)",
      }}
    >
      <div
        className="shop-toolbar"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "16px",
          paddingBottom: "24px",
          borderBottom: "1px solid var(--gray1)",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <select
            value={priceFilter}
            onChange={(event) => {
              setPriceFilter(event.target.value);
              resetToFirstPage();
            }}
            style={selectStyles}
          >
            {priceOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            value={ratingFilter}
            onChange={(event) => {
              setRatingFilter(event.target.value);
              resetToFirstPage();
            }}
            style={selectStyles}
          >
            {ratingOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <select
            value={sortBy}
            onChange={(event) => {
              setSortBy(event.target.value);
              resetToFirstPage();
            }}
            style={{ ...selectStyles, minWidth: "145px" }}
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            value={showCount}
            onChange={(event) => {
              setShowCount(Number(event.target.value));
              resetToFirstPage();
            }}
            style={{ ...selectStyles, minWidth: "110px" }}
          >
            {showOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div
        className="shop-active-bar"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "16px",
          padding: "16px 0 32px",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <Text size="px14" weight="w400" color="gray4">
            Active Filters:
          </Text>

          {activeFilters.length > 0 ? (
            activeFilters.map((filterLabel) => (
              <button
                key={filterLabel}
                type="button"
                onClick={() => {
                  if (
                    filterLabel ===
                    priceOptions.find((option) => option.value === priceFilter)
                      ?.label
                  ) {
                    setPriceFilter("all");
                  }

                  if (
                    filterLabel ===
                    ratingOptions.find((option) => option.value === ratingFilter)
                      ?.label
                  ) {
                    setRatingFilter("all");
                  }

                  resetToFirstPage();
                }}
                style={{
                  border: "none",
                  background: "transparent",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                <Text size="px14" weight="w600" color="greenG9">
                  {filterLabel}
                </Text>
                <span style={{ color: "var(--gray5)", fontSize: "14px" }}>
                  x
                </span>
              </button>
            ))
          ) : (
            <Text size="px14" weight="w400" color="gray5">
              No filters applied
            </Text>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <Text size="px14" weight="w600" color="gray5">
            {filteredProducts.length}
          </Text>
          <Text size="px14" weight="w400" color="gray5">
            Results found.
          </Text>
        </div>
      </div>

      <div
        className="shop-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 264px)",
          gap: "24px",
          justifyContent: "center",
        }}
      >
        {visibleProducts.map((product) => (
          <ProductCard key={product.id} product={product} size="medium" extraStyling={{borderRadius: '8px'}} />
        ))}
      </div>

      <div
        className="shop-pagination"
        style={{
          marginTop: "40px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "12px",
        }}
      >
        <button
          type="button"
          onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
          disabled={safeCurrentPage === 1}
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            border: "1px solid var(--gray1)",
            backgroundColor:
              safeCurrentPage === 1 ? "var(--gray05)" : "var(--white)",
            color: safeCurrentPage === 1 ? "var(--gray5)" : "var(--gray8)",
            cursor: safeCurrentPage === 1 ? "not-allowed" : "pointer",
            fontSize: "18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0",
            lineHeight: 1,
          }}
        >
          {"<"}
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          {paginationItems.map((item, index) =>
            item === "..." ? (
              <span
                key={`ellipsis-${index}`}
                style={{ color: "var(--gray5)", fontSize: "16px" }}
              >
                ...
              </span>
            ) : (
              <button
                key={item}
                type="button"
                onClick={() => setCurrentPage(item)}
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  border: "none",
                  backgroundColor:
                    item === safeCurrentPage
                      ? "var(--primary)"
                      : Math.abs(item - safeCurrentPage) === 1
                        ? "var(--gray05)"
                        : "transparent",
                  color:
                    item === safeCurrentPage ? "var(--white)" : "var(--gray7)",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: item === safeCurrentPage ? "600" : "400",
                }}
              >
                {item}
              </button>
            ),
          )}
        </div>

        <button
          type="button"
          onClick={() =>
            setCurrentPage((page) => Math.min(totalPages, page + 1))
          }
          disabled={safeCurrentPage === totalPages}
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            border: "1px solid var(--gray1)",
            backgroundColor:
              safeCurrentPage === totalPages ? "var(--gray05)" : "var(--white)",
            color:
              safeCurrentPage === totalPages ? "var(--gray3)" : "var(--gray8)",
            cursor: safeCurrentPage === totalPages ? "not-allowed" : "pointer",
            fontSize: "18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0",
            lineHeight: 1,
          }}
        >
          {">"}
        </button>
      </div>
    </div>
  );
}
